import { FC, useCallback } from "react";
import GridLayout, { Layout, LayoutItem } from "react-grid-layout";
import { absoluteStrategy } from "react-grid-layout/core";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { WidgetInstance, WidgetPosition } from "@/models/dashboard/Dashboard";
import { WidgetConfig } from "@/models/dashboard/WidgetConfig";
import WidgetWrapper from "./WidgetWrapper";
import { useDashboardGridContainerWidth } from "./useDashboardGridContainerWidth";
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_CONTAINER_PADDING,
  DASHBOARD_GRID_MARGIN,
  DASHBOARD_ROW_HEIGHT,
} from "../dashboard-grid-layout";
import DynamicListWidget from "../widgets/DynamicListWidget";
import StaticListWidget from "../widgets/StaticListWidget";
import ShortcutWidget from "../widgets/ShortcutWidget";
import SubscriptionsWidget from "../widgets/SubscriptionsWidget";
import NotificationsWidget from "../widgets/NotificationsWidget";
import FavoriteSearchesWidget from "../widgets/FavoriteSearchesWidget";
import RecentSearchesWidget from "../widgets/RecentSearchesWidget";
import FavoriteDocumentsWidget from "../widgets/FavoriteDocumentsWidget";
import {
  isDynamicListConfig,
  isStaticListConfig,
  isShortcutConfig,
  isSubscriptionsConfig,
  isNotificationsConfig,
  isFavoriteSearchesConfig,
  isRecentSearchesConfig,
  isFavoriteDocumentsConfig,
} from "@/models/dashboard/WidgetConfig";

interface DashboardGridProps {
  widgets: WidgetInstance[];
  isEditMode: boolean;
  /** The widget the editor panel is open on: ringed, while the others dim (LAR-726). */
  editingWidgetId?: string | null;
  onLayoutChange?: (widgets: WidgetInstance[]) => void;
  onWidgetConfigUpdate?: (widgetId: string, config: WidgetConfig) => void;
  onWidgetEdit?: (widgetId: string) => void;
  onWidgetDelete?: (widgetId: string) => void;
  onWidgetSaveAsTemplate?: (widgetId: string) => void;
  onWidgetCopyToOtherDashboard?: (widgetId: string) => void;
}

const DashboardGrid: FC<DashboardGridProps> = ({
  widgets,
  isEditMode,
  editingWidgetId = null,
  onLayoutChange,
  onWidgetConfigUpdate,
  onWidgetEdit,
  onWidgetDelete,
  onWidgetSaveAsTemplate,
  onWidgetCopyToOtherDashboard,
}) => {
  const { containerRef, containerWidth } = useDashboardGridContainerWidth();
  const layout: Layout = widgets.map((widget) => ({
    i: widget.id,
    x: widget.position.x,
    y: widget.position.y,
    w: widget.position.w,
    h: widget.position.h,
    minW: 3,
    minH: 2,
  }));

  // Handle layout changes from drag/resize
  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!isEditMode || !onLayoutChange) return;

      const updatedWidgets = widgets.map((widget) => {
        const layoutItem = newLayout.find((item: LayoutItem) => item.i === widget.id);
        if (layoutItem) {
          return {
            ...widget,
            position: {
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            } as WidgetPosition,
          };
        }
        return widget;
      });

      onLayoutChange(updatedWidgets);
    },
    [isEditMode, onLayoutChange, widgets]
  );

  // Render the appropriate widget component based on type
  const renderWidget = (widget: WidgetInstance) => {
    switch (widget.type) {
      case "dynamic-list":
        if (isDynamicListConfig(widget.config)) {
          return (
            <DynamicListWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
            />
          );
        }
        break;
      case "static-list":
        if (isStaticListConfig(widget.config)) {
          return (
            <StaticListWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
            />
          );
        }
        break;
      case "shortcut":
        if (isShortcutConfig(widget.config)) {
          return (
            <ShortcutWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
            />
          );
        }
        break;
      case "subscriptions":
        if (isSubscriptionsConfig(widget.config)) {
          return (
            <SubscriptionsWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
              isEditMode={isEditMode}
              onConfigUpdate={(config) => onWidgetConfigUpdate?.(widget.id, config)}
            />
          );
        }
        break;
      case "notifications":
        if (isNotificationsConfig(widget.config)) {
          return (
            <NotificationsWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
            />
          );
        }
        break;
      case "favorite-searches":
        if (isFavoriteSearchesConfig(widget.config)) {
          return (
            <FavoriteSearchesWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
              isEditMode={isEditMode}
              onConfigUpdate={(config) => onWidgetConfigUpdate?.(widget.id, config)}
            />
          );
        }
        break;
      case "recent-searches":
        if (isRecentSearchesConfig(widget.config)) {
          return (
            <RecentSearchesWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
            />
          );
        }
        break;
      case "favorite-documents":
        if (isFavoriteDocumentsConfig(widget.config)) {
          return (
            <FavoriteDocumentsWidget
              widgetId={widget.id}
              name={widget.name}
              config={widget.config}
              isEditMode={isEditMode}
              onConfigUpdate={(config) => onWidgetConfigUpdate?.(widget.id, config)}
            />
          );
        }
        break;
    }
    return <div className="text-muted-foreground">Unknown widget type</div>;
  };

  return (
    <div ref={containerRef} className="w-full">
      {containerWidth > 0 && (
        <div className="animate-in fade-in duration-200">
          <GridLayout
            className="layout"
            layout={layout}
            width={containerWidth}
            positionStrategy={absoluteStrategy}
            gridConfig={{
              cols: DASHBOARD_GRID_COLS,
              rowHeight: DASHBOARD_ROW_HEIGHT,
              margin: [...DASHBOARD_GRID_MARGIN],
              containerPadding: [...DASHBOARD_GRID_CONTAINER_PADDING],
            }}
            resizeConfig={{ enabled: isEditMode }}
            dragConfig={{ enabled: isEditMode, handle: ".widget-drag-handle" }}
            onLayoutChange={handleLayoutChange}
          >
            {widgets.map((widget) => (
              <div key={widget.id}>
                <WidgetWrapper
                  widget={widget}
                  isEditMode={isEditMode}
                  isBeingEdited={widget.id === editingWidgetId}
                  isDimmed={
                    editingWidgetId !== null && widget.id !== editingWidgetId
                  }
                  onEdit={() => onWidgetEdit?.(widget.id)}
                  onDelete={() => onWidgetDelete?.(widget.id)}
                  onSaveAsTemplate={() => onWidgetSaveAsTemplate?.(widget.id)}
                  onCopyToOtherDashboard={
                    onWidgetCopyToOtherDashboard
                      ? () => onWidgetCopyToOtherDashboard(widget.id)
                      : undefined
                  }
                >
                  {renderWidget(widget)}
                </WidgetWrapper>
              </div>
            ))}
          </GridLayout>
        </div>
      )}
    </div>
  );
};

export default DashboardGrid;
